import React, { useState, useEffect } from 'react';

let requestHandler = null;

function subscribe(handler) {
  requestHandler = handler;
}

function request(type, message, defaultValue) {
  return new Promise((resolve) => {
    if (!requestHandler) {
      // Respaldo por si el host aún no montó (no debería pasar en uso normal)
      if (type === 'prompt') resolve(window.prompt(message, defaultValue));
      else if (type === 'confirm') resolve(window.confirm(message));
      else { window.alert(message); resolve(true); }
      return;
    }
    requestHandler({ type, message, defaultValue, resolve });
  });
}

export const uiAlert = (message) => request('alert', message);
export const uiConfirm = (message) => request('confirm', message);
export const uiPrompt = (message, defaultValue = '') => request('prompt', message, defaultValue);
// Como un campo de monto de app bancaria: solo acepta números, ignora $ y comas
// tecleadas, y muestra separador de miles en vivo. Resuelve con el número limpio.
export const uiMoneyPrompt = (message, defaultValue = '') => request('money', message, String(defaultValue ?? ''));

// Formulario con varios campos en una sola ventana.
// fields: [{ name, label, type: 'text' | 'money' | 'select', value, options? }]
// options (solo para type 'select'): [{ value, label }]
// Resuelve con { [name]: valor } o null si se cancela.
export function uiForm(title, fields) {
  return new Promise((resolve) => {
    if (!requestHandler) {
      resolve(null);
      return;
    }
    requestHandler({ type: 'form', message: title, fields, resolve });
  });
}

// Deja solo dígitos y un punto decimal (ignora $, comas, letras, etc.)
function sanitizeMoneyRaw(value) {
  const stripped = String(value).replace(/[^\d.]/g, '');
  const parts = stripped.split('.');
  return parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : stripped;
}

// Formatea el raw ("200000" o "200000.5") con comas de miles para mostrarlo mientras se escribe
function formatMoneyDisplay(raw) {
  if (!raw) return '';
  const [intPart, decPart] = raw.split('.');
  const formattedInt = intPart ? Number(intPart).toLocaleString('es-MX') : '0';
  return decPart !== undefined ? `${formattedInt}.${decPart}` : formattedInt;
}

export function ModalHost() {
  const [dialog, setDialog] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [formValues, setFormValues] = useState({});

  useEffect(() => {
    subscribe((req) => {
      if (req.type === 'form') {
        const initial = {};
        req.fields.forEach((f) => {
          initial[f.name] = f.type === 'money' ? sanitizeMoneyRaw(String(f.value ?? '')) : (f.value ?? '');
        });
        setFormValues(initial);
      } else {
        const initial = req.type === 'money' ? sanitizeMoneyRaw(req.defaultValue ?? '') : (req.defaultValue ?? '');
        setInputValue(initial);
      }
      setDialog(req);
    });
    return () => subscribe(null);
  }, []);

  if (!dialog) return null;

  const close = (result) => {
    dialog.resolve(result);
    setDialog(null);
  };

  const isForm = dialog.type === 'form';
  const isTextInput = dialog.type === 'prompt' || dialog.type === 'money';
  const cancelValue = (isTextInput || isForm) ? null : false;

  const handleChange = (e) => {
    setInputValue(dialog.type === 'money' ? sanitizeMoneyRaw(e.target.value) : e.target.value);
  };

  const setFormField = (name, type, rawValue) => {
    setFormValues((prev) => {
      const next = { ...prev, [name]: type === 'money' ? sanitizeMoneyRaw(rawValue) : rawValue };
      // Selects "en cascada" (ej. Empresa -> Servicio, ver editSentReferral en Dashboard.js):
      // un campo puede declarar options como función de los valores actuales del formulario
      // en vez de una lista fija. Al cambiar cualquier campo, se recalculan esos selects
      // dependientes y si el valor que traían ya no es válido con las nuevas opciones
      // (cambiaste de empresa, por ejemplo), se limpia para no dejar un service_id
      // huérfano de otra empresa.
      if (dialog?.fields) {
        dialog.fields.forEach((f) => {
          if (f.type === 'select' && typeof f.options === 'function' && f.name !== name) {
            const opts = f.options(next) || [];
            if (!opts.some((o) => String(o.value) === String(next[f.name]))) {
              next[f.name] = opts[0]?.value ?? '';
            }
          }
        });
      }
      return next;
    });
  };

  return (
    <div className="modal-backdrop modal-backdrop--system" onClick={(e) => { if (e.target === e.currentTarget) close(cancelValue); }}>
      <div className="modal-box" role="dialog" aria-modal="true">
        <p className="modal-message">{dialog.message}</p>

        {isTextInput && (
          <input
            className="form-input"
            autoFocus
            inputMode={dialog.type === 'money' ? 'decimal' : 'text'}
            value={dialog.type === 'money' ? formatMoneyDisplay(inputValue) : inputValue}
            onChange={handleChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') close(inputValue);
              if (e.key === 'Escape') close(cancelValue);
            }}
          />
        )}

        {isForm && (
          <div>
            {dialog.fields.map((f, i) => (
              <div className="form-group" key={f.name}>
                <label className="form-label">{f.label}</label>
                {f.type === 'date' || f.type === 'time' ? (
                  <input
                    type={f.type}
                    className="form-input"
                    autoFocus={i === 0}
                    value={formValues[f.name] ?? ''}
                    onChange={(e) => setFormField(f.name, f.type, e.target.value)}
                  />
                ) : f.type === 'select' ? (
                  <select
                    className="form-select"
                    autoFocus={i === 0}
                    value={formValues[f.name] ?? ''}
                    onChange={(e) => setFormField(f.name, f.type, e.target.value)}
                  >
                    {(typeof f.options === 'function' ? f.options(formValues) : (f.options || [])).map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="form-input"
                    autoFocus={i === 0}
                    inputMode={f.type === 'money' ? 'decimal' : 'text'}
                    value={f.type === 'money' ? formatMoneyDisplay(formValues[f.name] ?? '') : (formValues[f.name] ?? '')}
                    onChange={(e) => setFormField(f.name, f.type, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') close(formValues);
                      if (e.key === 'Escape') close(cancelValue);
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        <div className="modal-actions">
          {dialog.type !== 'alert' && (
            <button className="btn btn-secondary" onClick={() => close(cancelValue)}>Cancelar</button>
          )}
          <button
            className="btn btn-primary"
            autoFocus={!isTextInput && !isForm}
            onClick={() => close(isForm ? formValues : isTextInput ? inputValue : true)}
          >
            {dialog.type === 'alert' ? 'Entendido' : isForm ? 'Guardar' : 'Aceptar'}
          </button>
        </div>
      </div>
    </div>
  );
}
